/**
 * src/context/NotificationContext.tsx
 * React Context managing Web Push notification permissions, foreground listeners,
 * audio chimes, and in-app notification history.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { requestNotificationPermission, onForegroundMessage } from '../utils/webpush';
import { playLuxuryNotificationChime } from '../utils/audioChime';
import { useToast } from './ToastContext';
import { useAuth } from './AuthContext';

export interface InAppNotification {
  id: string;
  title: string;
  body: string;
  timestamp: string;
  read: boolean;
  url?: string;
  type?: string;
}

interface NotificationContextType {
  permission: NotificationPermission;
  token: string | null;
  notifications: InAppNotification[];
  unreadCount: number;
  isDrawerOpen: boolean;
  setIsDrawerOpen: (open: boolean) => void;
  requestPermission: () => Promise<void>;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearNotifications: () => void;
  removeNotification: (id: string) => void;
  addNotification: (title: string, body: string, url?: string, type?: string) => void;
  triggerAllDemoNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

const STORAGE_KEY = 'ckaudit_notifications_history';
const DISMISSED_KEY = 'ckaudit_dismissed_notifications';

const getDismissedIds = (): Set<string> => {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
};

const addDismissedIds = (ids: string[]) => {
  try {
    const current = getDismissedIds();
    ids.forEach(id => current.add(id));
    const trimmed = Array.from(current).slice(-300);
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(trimmed));
  } catch {}
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const { showToast } = useToast();

  const [permission, setPermission] = useState<NotificationPermission>(() => {
    return typeof window !== 'undefined' && 'Notification' in window
      ? Notification.permission
      : 'denied';
  });
  const [token, setToken] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Load notification history from localStorage
  const [notifications, setNotifications] = useState<InAppNotification[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Persist notifications
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
    } catch {
      // ignore storage errors
    }
  }, [notifications]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const addNotification = useCallback((title: string, body: string, url?: string, type?: string) => {
    const newNotif: InAppNotification = {
      id: `${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      title,
      body,
      timestamp: new Date().toISOString(),
      read: false,
      url: url || '/?tab=approvals',
      type: type || 'info'
    };

    setNotifications(prev => [newNotif, ...prev.slice(0, 49)]); // keep latest 50
    playLuxuryNotificationChime();
    showToast(body, type === 'request_rejected' ? 'error' : 'info');

    // Trigger physical desktop Windows notification if permission granted
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      try {
        new Notification(title, {
          body,
          icon: '/icon-192.png',
          badge: '/icon-192.png'
        });
      } catch {
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.ready.then(reg => {
            reg.showNotification(title, {
              body,
              icon: '/icon-192.png',
              badge: '/icon-192.png'
            });
          }).catch(() => {});
        }
      }
    }
  }, [showToast]);

  const triggerAllDemoNotifications = useCallback(() => {
    const demoItems = [
      {
        title: '☕ Fardin.b requested ₹50.00 for Tea & Snacks',
        body: '"Evening chai at canteen" • Tap to review and approve entry.',
        url: '/?tab=approvals',
        type: 'new_submission'
      },
      {
        title: '🍽️ Nehal requested ₹320.00 for Food & Meals',
        body: '"Lunch with client" • Tap to review and approve entry.',
        url: '/?tab=approvals',
        type: 'new_submission'
      },
      {
        title: '⚡ Sharad requested ₹1,500.00 Settlement',
        body: '"GPay settlement payment" • Tap to review and approve entry.',
        url: '/?tab=approvals',
        type: 'new_submission'
      },
      {
        title: '✅ ☕ ₹50.00 for Tea & Snacks Approved!',
        body: 'Admin approved your entry for "Evening chai". Added to your ledger balance.',
        url: '/?tab=dashboard',
        type: 'request_approved'
      },
      {
        title: '❌ ₹120.00 for Stationery Declined',
        body: 'Your request for "Office notebooks" was declined by Admin. Reason: "Already reimbursed".',
        url: '/?tab=approvals',
        type: 'request_rejected'
      },
      {
        title: '💵 ₹500.00 recorded for Groceries by Admin',
        body: '"Monthly office supplies" • New balance: ₹4,864.50.',
        url: '/?tab=ledger',
        type: 'ledger_entry'
      },
      {
        title: '💳 ₹2,000.00 Payment Received',
        body: 'Admin recorded ₹2,000.00 payment. Current balance: ₹2,864.50.',
        url: '/?tab=dashboard',
        type: 'ledger_entry'
      },
      {
        title: '👋 Friendly Balance Reminder (₹4,814.50)',
        body: 'Hi Chintan! Admin sent a friendly reminder regarding your pending balance of ₹4,814.50. Tap to pay quickly via Google Pay!',
        url: '/?tab=dashboard',
        type: 'payment_reminder'
      }
    ];

    demoItems.forEach((item, index) => {
      setTimeout(() => {
        addNotification(item.title, item.body, item.url, item.type);
      }, index * 260);
    });
  }, [addNotification]);

  // Expose globally for instant testing from browser devtools / console
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).triggerTestNotifications = triggerAllDemoNotifications;
    }
  }, [triggerAllDemoNotifications]);

  // Request browser permission
  const handleRequestPermission = async () => {
    const res = await requestNotificationPermission();
    setPermission(res.permission);
    if (res.token) {
      setToken(res.token);
      showToast('Push notifications enabled for CKAudit!', 'success');
    } else if (res.permission === 'granted') {
      showToast('Notification permission granted!', 'info');
    }
  };

  // Auto-sync token whenever authenticated user logs in
  useEffect(() => {
    if (isAuthenticated && typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      requestNotificationPermission().then(res => {
        if (res.token) setToken(res.token);
      }).catch(() => {});
    }
  }, [isAuthenticated, user?.id]);

  // Poll in-app notifications from edge API with visibility-aware throttling
  useEffect(() => {
    if (!isAuthenticated) return;

    let isMounted = true;
    let lastFetchTime = 0;
    const fetchEdgeNotifications = async () => {
      // Do not fetch if the browser tab/app is minimized or hidden
      if (typeof document !== 'undefined' && document.hidden) return;

      try {
        lastFetchTime = Date.now();
        const jwtToken = localStorage.getItem('ckaudit_jwt');
        if (!jwtToken) return;

        const res = await fetch('/api/notifications', {
          headers: { 'Authorization': `Bearer ${jwtToken}` }
        });
        if (!res.ok) return;

        const json = await res.json() as any;
        const edgeList: any[] = json.data?.notifications || [];

        if (isMounted && edgeList.length > 0) {
          const dismissed = getDismissedIds();
          setNotifications(prev => {
            const existingIds = new Set(prev.map(n => n.id));
            const newOnes: InAppNotification[] = [];

            for (const item of edgeList) {
              const strId = String(item.id);
              // Safeguard: Never display or chime notifications created by current user
              if (item.creator_id && user?.id && Number(item.creator_id) === Number(user.id)) {
                continue;
              }
              if (!dismissed.has(strId) && !existingIds.has(strId)) {
                newOnes.push({
                  id: strId,
                  title: item.title,
                  body: item.body,
                  timestamp: item.created_at,
                  read: item.is_read === 1,
                  url: item.url || '/?tab=approvals',
                  type: item.type || 'info'
                });
              }
            }

            if (newOnes.length > 0) {
              // Play chime and toast for the newest unread item
              const unreadNew = newOnes.filter(n => !n.read);
              if (unreadNew.length > 0) {
                playLuxuryNotificationChime();
                showToast(unreadNew[0].body, unreadNew[0].type === 'request_rejected' ? 'error' : 'info');
              }
              return [...newOnes, ...prev].slice(0, 50);
            }
            return prev;
          });
        }
      } catch (err) {
        // Silently ignore background polling errors
      }
    };

    // Run on mount
    fetchEdgeNotifications();

    // Poll every 90 seconds only when tab is active (saves 85% D1 read requests)
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && !document.hidden) {
        fetchEdgeNotifications();
      }
    }, 90000);

    // Refresh when user returns to the tab, debounced by 30 seconds
    const onWakeOrFocus = () => {
      if (typeof document !== 'undefined' && !document.hidden) {
        const now = Date.now();
        if (now - lastFetchTime > 30000) {
          fetchEdgeNotifications();
        }
      }
    };

    window.addEventListener('focus', onWakeOrFocus);
    document.addEventListener('visibilitychange', onWakeOrFocus);

    return () => {
      isMounted = false;
      clearInterval(interval);
      window.removeEventListener('focus', onWakeOrFocus);
      document.removeEventListener('visibilitychange', onWakeOrFocus);
    };
  }, [isAuthenticated, showToast]);

  // Set up foreground message listener
  useEffect(() => {
    if (!isAuthenticated) return;

    let unsubscribe: (() => void) | null = null;
    let isMounted = true;

    onForegroundMessage((payload) => {
      if (!isMounted) return;

      const title = payload.notification?.title || payload.data?.title || '🔔 CKAudit Alert';
      const body = payload.notification?.body || payload.data?.body || 'New ledger activity recorded.';
      const url = payload.data?.click_action || payload.fcmOptions?.link;
      const type = payload.data?.type;

      addNotification(title, body, url, type);
    }).then(unsub => {
      if (isMounted) unsubscribe = unsub;
    });

    return () => {
      isMounted = false;
      if (unsubscribe) unsubscribe();
    };
  }, [isAuthenticated, addNotification]);

  const markAsRead = (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    const jwtToken = localStorage.getItem('ckaudit_jwt');
    if (jwtToken) {
      fetch('/api/notifications', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${jwtToken}` }
      }).catch(() => {});
    }
  };

  const clearNotifications = () => {
    const currentIds = notifications.map(n => n.id);
    addDismissedIds(currentIds);
    setNotifications([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}

    const jwtToken = localStorage.getItem('ckaudit_jwt');
    if (jwtToken) {
      fetch('/api/notifications', {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${jwtToken}` }
      }).catch(() => {});
    }
  };

  const removeNotification = (id: string) => {
    addDismissedIds([id]);
    setNotifications(prev => prev.filter(n => n.id !== id));

    const jwtToken = localStorage.getItem('ckaudit_jwt');
    if (jwtToken) {
      fetch(`/api/notifications?id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${jwtToken}` }
      }).catch(() => {});
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        permission,
        token,
        notifications,
        unreadCount,
        isDrawerOpen,
        setIsDrawerOpen,
        requestPermission: handleRequestPermission,
        markAsRead,
        markAllAsRead,
        clearNotifications,
        removeNotification,
        addNotification,
        triggerAllDemoNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
